import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePurchaseOrder } from '../../hooks/usePurchaseOrders';
import { usePurchaseOrderPayments } from '../../hooks/usePayments';
import { useAccounts } from '../../hooks/useAccounts';
import { purchaseOrdersService } from '../../services/purchaseOrdersService';
import { paymentsService } from '../../services/paymentsService';
import { accountsService } from '../../services/accountsService';
import { contactsService } from '../../services/contactsService';
import { formatCurrency } from '../../utils/currency';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import FileUpload from '../../components/ui/FileUpload';
import AccountSelect from '../../components/ui/AccountSelect';

const PurchaseOrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { order, loading, error } = usePurchaseOrder(id || null);
  const { payments, loading: paymentsLoading } = usePurchaseOrderPayments(id || null);
  const { accounts, loading: accountsLoading } = useAccounts();
  const { businessId } = useAuth();

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<string | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [paymentAttachment, setPaymentAttachment] = useState<File | null>(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Calculate total paid from payments
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const amountDue = order ? order.total_amount - totalPaid : 0;

  const handleDelete = async () => {
    // Double-check status in case button wasn't properly disabled
    if (order?.status === 'paid') {
      alert('Cannot delete purchase order because there are payments related to this purchase order. Please delete all payments first.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this purchase order? This action cannot be undone.')) {
      try {
        const { error: deleteError } = await purchaseOrdersService.deletePurchaseOrder(id!, businessId || undefined);
        if (deleteError) {
          alert(`Failed to delete purchase order: ${deleteError.message}`);
        } else {
          navigate('/purchase-orders');
        }
      } catch (err) {
        alert('Failed to delete purchase order');
      }
    }
  };

  // Handle attachment viewing
  const openAttachmentModal = (attachmentUrl: string) => {
    setSelectedAttachment(attachmentUrl);
    setIsAttachmentModalOpen(true);
  };

  const closeAttachmentModal = () => {
    setIsAttachmentModalOpen(false);
    setSelectedAttachment(null);
  };

  // Payment modal handlers
  const openPaymentModal = () => {
    setPaymentAmount(amountDue.toString());
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setSelectedAccountId('');
    setPaymentDescription('');
    setPaymentAttachment(null);
    setPaymentError('');
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
  };

  // Handle payment deletion with balance adjustments
  const handleDeletePayment = async (paymentId: string, amount: number, accountId: string | undefined) => {
    if (!window.confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete the payment using atomic operations
      const { error: deleteError } = await paymentsService.deletePayment(paymentId, businessId || undefined);
      if (deleteError) {
        throw deleteError;
      }

      // Reverse the balance adjustments
      if (accountId) {
        const selectedAccount = accounts.find(acc => acc.id === accountId);
        if (selectedAccount) {
          // Add back the amount to account balance (since we're reversing the payment)
          const newBalance = selectedAccount.balance + amount;
          await accountsService.updateAccountBalance(accountId, newBalance);
        }
      }

      // Reverse vendor balance adjustment
      if (order && order.vendor?.id) {
        // Add back the amount to vendor balance (since we're reversing the payment)
        const newVendorBalance = (order.vendor.balance || 0) + amount;
        await contactsService.updateContactBalance(order.vendor.id, newVendorBalance);
      }

      // Check if purchase order status should change back to 'unpaid'
      if (order) {
        const newAmountDue = amountDue + amount; // Add back the deleted payment amount
        if (newAmountDue > 0 && order.status === 'paid') {
          const { error: statusError } = await purchaseOrdersService.updatePurchaseOrderStatus(
            order.id,
            'unpaid',
            businessId || undefined
          );
          if (statusError) {
            console.error('Failed to update purchase order status:', statusError);
          }
        }
      }

      // Refresh the page to show updated amounts
      window.location.reload();
    } catch (err) {
      alert(`Failed to delete payment: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setPaymentError('');
      setSavingPayment(true);

      const paymentAmountValue = parseFloat(paymentAmount);
      if (isNaN(paymentAmountValue) || paymentAmountValue <= 0) {
        throw new Error('Please enter a valid payment amount');
      }

      if (paymentAmountValue > amountDue) {
        throw new Error(`Payment amount cannot exceed the amount due (${amountDue.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })})`);
      }

      if (!selectedAccountId) {
        throw new Error('Please select an account');
      }

      let attachmentUrl: string | undefined;

      // Upload attachment if provided
      if (paymentAttachment) {
        try {
          attachmentUrl = await paymentsService.uploadPaymentAttachment(
            paymentAttachment,
            undefined, // Will be set after payment creation
            businessId || undefined
          );
        } catch (uploadError) {
          console.warn('Failed to upload attachment, continuing without it:', uploadError);
          // Continue without attachment if upload fails
        }
      }

      const paymentData = {
        purchase_order_id: id,
        account_id: selectedAccountId,
        amount: paymentAmountValue,
        payment_date: paymentDate,
        description: paymentDescription || undefined,
        attachment_url: attachmentUrl,
      };

      const { payment, error: createError } = await paymentsService.createPayment(paymentData, businessId || undefined);

      if (createError) {
        throw createError;
      }

      // Close modal and refresh the page
      closePaymentModal();
      window.location.reload();
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Failed to save payment');
    } finally {
      setSavingPayment(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Extract filename from URL
  const getFileNameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return pathParts[pathParts.length - 1];
    } catch {
      return 'Unknown File';
    }
  };

  // Check if file is an image
  const isImageFile = (filename: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error || 'Purchase order not found'}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Purchase Orders', href: '/purchase-orders' },
    { label: `PO-${order.po_number}`, current: true }
  ];

  return (
    <div>
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Purchase Order {order.po_number}
          </h1>
          <p className="text-gray-600">
            Created on {new Date(order.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex space-x-2">
          {amountDue > 0 && (
            <Button
              variant="primary"
              onClick={openPaymentModal}
            >
              Add Payment
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/purchase-orders/${order.id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="secondary"
            onClick={handleDelete}
            disabled={order.status === 'paid'}
            className={`bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 ${
              order.status === 'paid' ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={
              order.status === 'paid'
                ? 'Cannot delete purchase order because there are payments related to this purchase order'
                : 'Delete this purchase order'
            }
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Purchase Order Details</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500">Purchase Number</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.po_number}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Purchase Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(order.purchase_date).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Vendor</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.vendor?.name || 'Unknown Vendor'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Status</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`badge ${purchaseOrdersService.getStatusBadgeClass(order.status)}`}>
                    {purchaseOrdersService.getStatusLabel(order.status)}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Created</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(order.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(order.updated_at).toLocaleDateString()}
                </dd>
              </div>
            </div>

            {order.description && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.description}</dd>
              </div>
            )}

            {/* Items Section */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Order Items</h4>
              {order.items && order.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Discount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.items.map((item: any) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.item_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(item.unit_price)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatCurrency(item.discount || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatCurrency(item.total_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No items found in this purchase order.
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-900 mb-4">Order Summary</h4>
              <div className="bg-gray-50 rounded-lg p-6">
                {/* Main Summary Row */}
                <div className="grid grid-cols-1 gap-3 mb-4">
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-medium text-gray-700">Subtotal</dt>
                    <dd className="text-lg font-semibold text-gray-900">
                      {formatCurrency(order.total_amount + order.discount_amount)}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-medium text-gray-700">Discount</dt>
                    <dd className="text-lg font-semibold text-red-600">
                      -{formatCurrency(order.discount_amount)}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-medium text-gray-700">Paid</dt>
                    <dd className="text-lg font-semibold text-green-600">
                      {formatCurrency(totalPaid)}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-sm font-medium text-gray-700">Amount Due</dt>
                    <dd className="text-lg font-semibold text-orange-600">
                      {formatCurrency(Math.max(0, amountDue))}
                    </dd>
                  </div>
                </div>

                {/* Total Row */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <dt className="text-lg font-medium text-gray-900">Total Amount</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      {formatCurrency(order.total_amount)}
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Payments Section */}
            <div className="mt-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-md font-medium text-gray-900">Payments</h4>
                {amountDue > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openPaymentModal}
                  >
                    Add Payment
                  </Button>
                )}
              </div>

              {paymentsLoading ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
                </div>
              ) : payments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payments.map((payment) => (
                        <tr key={payment.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {payment.account?.name || 'Unknown Account'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            {formatCurrency(payment.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {payment.description || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex space-x-2">
                              {payment.attachment_url && (
                                <button
                                  onClick={() => openAttachmentModal(payment.attachment_url!)}
                                  className="text-blue-600 hover:text-blue-900 text-sm"
                                  title="View attachment"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                </button>
                              )}
                              <button
                                onClick={() => handleDeletePayment(payment.id, payment.amount, payment.account_id)}
                                className="text-red-600 hover:text-red-900 text-sm"
                                title="Delete payment"
                              >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No payments recorded for this purchase order.</p>
                  {amountDue > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openPaymentModal}
                      className="mt-2"
                    >
                      Add First Payment
                    </Button>
                  )}
                  {amountDue <= 0 && (
                    <p className="mt-2 text-green-600 font-medium">This purchase order is fully paid.</p>
                  )}
                </div>
              )}
            </div>

            {/* Attachments Section */}
            {order.attachment_urls && order.attachment_urls.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-gray-900">Attachments</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAttachmentModalOpen(true)}
                  >
                    View All Attachments
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {order.attachment_urls.slice(0, 4).map((url: string, index: number) => {
                    const filename = getFileNameFromUrl(url);
                    return (
                      <div
                        key={index}
                        className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => openAttachmentModal(url)}
                      >
                        <div className="flex-shrink-0">
                          {isImageFile(filename) ? (
                            <svg className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          ) : (
                            <svg className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {filename}
                          </p>
                          <p className="text-xs text-gray-500">
                            Click to view
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {order.attachment_urls.length > 4 && (
                    <div className="flex items-center justify-center p-3 bg-gray-100 rounded-lg">
                      <span className="text-sm text-gray-600">
                        +{order.attachment_urls.length - 4} more files
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Vendor Information</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{order.vendor?.name || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Email</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.vendor?.email || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Phone</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.vendor?.phone || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Address</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {order.vendor?.address || 'N/A'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Balance</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900">
                  {formatCurrency(order.vendor?.balance || 0)}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attachment Viewing Modal */}
      {isAttachmentModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closeAttachmentModal}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Purchase Order Attachments
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={closeAttachmentModal}
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {selectedAttachment ? (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      {selectedAttachment && isImageFile(getFileNameFromUrl(selectedAttachment)) ? (
                        <img
                          src={selectedAttachment}
                          alt={getFileNameFromUrl(selectedAttachment)}
                          className="max-w-full max-h-96 object-contain rounded-lg"
                        />
                      ) : (
                        <div className="flex flex-col items-center p-8 bg-gray-50 rounded-lg">
                          <svg className="h-16 w-16 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-gray-600 mb-2">Document Preview Not Available</p>
                          <p className="text-sm text-gray-500">Click download to view this file</p>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-center space-x-4">
                      <a
                        href={selectedAttachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {order.attachment_urls?.map((url: string, index: number) => {
                      const filename = getFileNameFromUrl(url);
                      return (
                        <div
                          key={index}
                          className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => openAttachmentModal(url)}
                        >
                          <div className="flex-shrink-0">
                            {isImageFile(filename) ? (
                              <svg className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            ) : (
                              <svg className="h-10 w-10 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-3 flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              Click to view
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeAttachmentModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={closePaymentModal}></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Add Payment - PO-{order.po_number}
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={closePaymentModal}
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handlePaymentSubmit}>
                  {paymentError && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                      {paymentError}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Amount
                      </label>
                      <Input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        min="0"
                        max={amountDue}
                        step="1000"
                        required
                        placeholder="Enter payment amount"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Maximum amount: {amountDue.toLocaleString('id-ID', { style: 'currency', currency: 'IDR' })}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Date
                      </label>
                      <Input
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <AccountSelect
                        accounts={accounts}
                        value={selectedAccountId}
                        onChange={setSelectedAccountId}
                        label="Account"
                        placeholder="Select Account"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description (Optional)
                      </label>
                      <textarea
                        value={paymentDescription}
                        onChange={(e) => setPaymentDescription(e.target.value)}
                        rows={3}
                        className="input-field"
                        placeholder="Payment notes or description..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Receipt (Optional)
                      </label>
                      <FileUpload
                        onFileSelect={(file) => setPaymentAttachment(file)}
                        accept="image/*,.pdf,.doc,.docx"
                        maxSize={5 * 1024 * 1024} // 5MB
                        placeholder="Upload payment receipt or supporting document"
                      />
                      {paymentAttachment && (
                        <p className="mt-2 text-sm text-green-600">
                          Selected: {paymentAttachment.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={closePaymentModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={savingPayment}
                    >
                      {savingPayment ? 'Saving Payment...' : 'Save Payment'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetails;